import { EnumVirtualProperty, MIVConstants, ModelCompute, MRealObject, MTargetsList, MVirtualObject, QueryBuilder, StringRealProperty, StringVirtualProperty, VirtualList } from "@1oo4/miv";
import { MChapter, MArticle, MSection, MContentWithText } from "./mdoc";
import { DocClient } from "../docclient";
import { VLineBreak, VLink, VRichArticle, VRichChapter, VRichSection, VRichTextItem, VSpan } from "../../pages/doc/richtexts";
import { VContent } from "../../pages/doc/contents";

export class RichTextParser {

    private builder: string = "";
    private symbol: string = "";
    private bold: number = 0;
    private italic: number = 0;
    private underline: number = 0;
    private code: number = 0;
    private parsing_symbol: boolean = false;
    private parsing_name: boolean = false;
    private escape: boolean = false;
    private readonly linebreak: RichLineBreak = new RichLineBreak();
    private readonly spans: SpanCache = new SpanCache();
    private readonly brackets: BracketCache = new BracketCache();
    private readonly chaptersFetcher: Map<MChapter, number> = new Map();
    private readonly articlesFetcher: Map<MArticle, number> = new Map();
    private readonly sectionsFetcher: Map<MSection, number> = new Map();
    private readonly chaptersList: Array<MChapter> = new Array();
    private readonly articlesList: Array<MArticle> = new Array();
    private readonly sectionsList: Array<MSection> = new Array();
    readonly result: Array<RichTextItem> = new Array();

    constructor() {

    }

    parse(text: string | undefined) {
        this.result.length = 0;
        if (text === undefined || text.length <= 0) {
            return;
        }
        this.spans.reset();
        this.brackets.reset();
        this.bold = 0;
        this.italic = 0;
        this.underline = 0;
        this.code = 0;
        let i = 0;
        while (i < text.length) {
            const c = text[i];
            if (this.parsing_name) {
                switch (c) {
                    case "\\":
                        if (this.escape) {
                            this.builder += "\\";
                            this.escape = false;
                        } else {
                            this.escape = true;
                        }
                        break;
                    case "}":
                        if (this.escape) {
                            this.escape = false;
                            this.builder += "}";
                        } else {
                            this.builder = this.builder.trim();
                            if (this.builder.length > 0) {
                                this.result.push(this.brackets.get(this.symbol, this.builder));
                                this.builder = "";
                            } else {
                                this.builder = "{" + this.symbol + "-}";
                            }
                            this.symbol = "";
                            this.parsing_name = false;
                        }
                        break;
                    default:
                        if (this.escape) {
                            this.escape = false;
                            this.builder = this.builder + "\\" + c;
                        } else {
                            this.builder += c;
                        }
                        break;
                }
                i++;
            } else if (this.parsing_symbol) {
                if (c == "-") {
                    if (this.symbol.length > 0) {
                        this.parsing_name = true;
                    } else {
                        this.builder = "{-";
                    }
                    this.parsing_symbol = false;
                    i++;
                } else if (("a" <= c && c <= "z") || ("A" <= c && c <= "Z")) {
                    this.symbol += c;
                    i++;
                } else {
                    this.parsing_symbol = false;
                    this.builder = "{" + this.symbol;
                    this.symbol = "";
                }
            } else {
                let prvi = i;
                if (this.escape) {
                    if (c == "[" || c == "{") {
                        this.builder = this.builder + c;
                    } else {
                        this.builder = this.builder + "\\" + c;
                    }
                    this.escape = false;
                    i++;
                } else {
                    switch (c) {
                        case "\r":
                        case "\t":
                            i++;
                            break;
                        case "\\":
                            this.escape = true;
                            i++;
                            break;
                        case "\n":
                            this.closeBuilder();
                            this.result.push(this.linebreak);
                            i++;
                            break;
                        case "[":
                            //#region STYLING
                            if (i < text.length - 2) {
                                const nxt = text[i + 1];
                                if (nxt == "/") {
                                    if (i < text.length - 3) {
                                        const styleSymbol = text[i + 2];
                                        if ((styleSymbol == "c" || styleSymbol == "u" || styleSymbol == "b" || styleSymbol == "i") && text[i + 3] == "]") {
                                            this.closeBuilder();
                                            if (styleSymbol == "c") {
                                                if (this.code > 0) this.code--;
                                            } if (styleSymbol == "u") {
                                                if (this.underline > 0) this.underline--;
                                            } else if (styleSymbol == "b") {
                                                if (this.bold > 0) this.bold--;
                                            } else {
                                                if (this.italic > 0) this.italic--;
                                            }
                                            i += 4;
                                        }
                                    }
                                } else if ((nxt == "c" || nxt == "u" || nxt == "b" || nxt == "i") && text[i + 2] == "]") {
                                    this.closeBuilder();
                                    if (nxt == "c") {
                                        this.code++;
                                    } else if (nxt == "u") {
                                        this.underline++;
                                    } else if (nxt == "b") {
                                        this.bold++;
                                    } else {
                                        this.italic++;
                                    }
                                    i += 3;
                                }
                            }
                            //#endregion
                            break;
                        case "{":
                            //#region BRACKET
                            if (i < text.length - 2) {
                                this.closeBuilder();
                                this.parsing_symbol = true;
                                i++;
                            }
                            //#endregion
                            break;
                    }
                }
                if (prvi == i) {
                    this.builder = this.builder + c;
                    i++;
                }
            }
        }
        if (this.parsing_name) {
            this.builder = "{" + this.symbol + "-" + this.builder;
            this.symbol = "";
        } else if (this.parsing_symbol) {
            this.builder = "{" + this.symbol;
            this.symbol = "";
        }
        this.closeBuilder();
    }

    private closeBuilder() {
        if (this.escape) {
            this.escape = false;
            this.builder += "\\";
        }
        if (this.builder.length > 0) {
            let style: RichTextStyle = RichTextStyle.NONE;
            if (this.bold > 0) {
                style = style | RichTextStyle.BOLD;
            }
            if (this.italic > 0) {
                style = style | RichTextStyle.ITALIC;
            }
            if (this.underline > 0) {
                style = style | RichTextStyle.UNDERLINE;
            }
            if (this.code > 0) {
                style = style | RichTextStyle.CODE;
            }
            if (this.result.length <= 0 || !this.result[this.result.length - 1].appendText(this.builder, style)) {
                this.result.push(this.spans.get(style, this.builder));
            }
            this.builder = "";
        }
    }

    parseM(text: string | undefined, owner: MContentWithText, items: VirtualList<MRichTextItem>, mc: ModelCompute) {
        this.parse(text);
        const mlist = window.client!.richMListTool;
        for (let i = 0; i < this.result.length; i++) {
            mlist.push(this.result[i].toM(owner, mc));
        }
        mc.DefineArray(items, mlist);
        mlist.length = 0;
    }

    setText(prop: StringRealProperty, content: MContentWithText, qb: QueryBuilder, txt: string | undefined, allProps: StringRealProperty[] | undefined) {
        txt = txt?.trim();
        if (txt === undefined) {
            qb.ClearValue(prop);
            return;
        }
        if (allProps !== undefined) {
            for (let i = 0; i < allProps.length; i++) {
                const p = allProps[i];
                if (p !== prop) {
                    this.parse(p.Value);
                    for (let j = 0; j < this.result.length; j++) {
                        this.result[j].analyzeReferences(content, this.chaptersList, this.articlesList, this.sectionsList);
                    }
                }
            }
        }
        this.parse(txt);
        for (let i = 0; i < this.result.length; i++) {
            this.result[i].analyzeReferences(content, this.chaptersList, this.articlesList, this.sectionsList);
        }
        RichTextParser.manageRefsList(content.ChapterRefs, this.chaptersList, this.chaptersFetcher, content, qb);
        RichTextParser.manageRefsList(content.ArticleRefs, this.articlesList, this.articlesFetcher, content, qb);
        RichTextParser.manageRefsList(content.SectionRefs, this.sectionsList, this.sectionsFetcher, content, qb);
        let result = "";
        for (let i = 0; i < this.result.length; i++) {
            result += this.result[i].setText((i > 0) ? this.result[i - 1] : undefined, (i < this.result.length - 1) ? this.result[i + 1] : undefined, this.chaptersFetcher, this.articlesFetcher, this.sectionsFetcher);
        }
        qb.SetStringValue(prop, result);
        this.chaptersList.length = 0;
        this.articlesList.length = 0;
        this.sectionsList.length = 0;
        this.chaptersFetcher.clear();
        this.articlesFetcher.clear();
        this.sectionsFetcher.clear();
    }
    private static manageRefsList<T extends MRealObject>(data: MTargetsList<T>, required: Array<T>, fetcher: Map<T, number>, owner: MContentWithText, qb: QueryBuilder) {
        let offset = 0;
        for (let i = 0; i < data.Count; i++) {
            const existing = data.Get(i);
            let found = false;
            for (let j = 0; j < required.length; j++) {
                if (existing == required[j]) {
                    found = true;
                    fetcher.set(existing, 1 + i + offset);
                    break;
                }
            }
            if (!found) {
                offset -= 1;
                qb.ClearRelation(owner, MIVConstants.PROPERTY_GENERIC_RELATION, existing);
            }
        }
        let new_count = 0;
        for (let i = 0; i < required.length; i++) {
            const r = required[i];
            if (!fetcher.has(r)) {
                qb.AppendRelationGeneric(owner, MIVConstants.TYPE_GENERIC_RELATION, MIVConstants.PROPERTY_GENERIC_RELATION, r);
                new_count++;
                fetcher.set(r, new_count + data.Count + offset);
            }
        }
    }
}

export enum RichTextStyle {
    NONE = 0, BOLD = 1, UNDERLINE = 2, ITALIC = 4, BOLD_UNDERLINE = 3, BOLD_ITALIC = 5, UNDERLINE_ITALIC = 6, BOLD_ITALIC_UNDERLINE = 7, CODE = 8
}

export abstract class RichTextItem {
    constructor() {

    }
    abstract appendText(txt: string, style: RichTextStyle): boolean;
    abstract get isBold(): boolean;
    abstract get isUnderline(): boolean;
    abstract get isItalic(): boolean;
    abstract get isCode(): boolean;
    abstract toM(owner: MContentWithText, mc: ModelCompute): MRichTextItem;
    abstract analyzeReferences(owner: MContentWithText, chapters: Array<MChapter>, articles: Array<MArticle>, sections: Array<MSection>): void;
    abstract setText(prv: RichTextItem | undefined, nxt: RichTextItem | undefined, chapters: Map<MChapter, number>, article: Map<MArticle, number>, section: Map<MSection, number>): string | undefined;
}

abstract class RichTextCache<T extends RichTextItem> {
    private _count_doc_: number = 0;
    private _list_doc_: Array<T> = new Array();

    constructor() {

    }

    reset() {
        this._count_doc_ = 0;
    }

    protected GetItem(): T {
        let result: T;
        if (this._count_doc_ < this._list_doc_.length) {
            result = this._list_doc_[this._count_doc_];
        } else {
            result = this.cstr();
            this._list_doc_.push(result);
        }
        this._count_doc_++;
        return result;
    }

    protected abstract cstr(): T;
}

export class RichSpan extends RichTextItem {
    private style: RichTextStyle = RichTextStyle.NONE;
    private text: string = "";
    constructor() {
        super();
    }
    setup(style: RichTextStyle, text: string) {
        this.style = style;
        this.text = text;
    }
    get isBold(): boolean { return (this.style & RichTextStyle.BOLD) != 0; }
    get isItalic(): boolean { return (this.style & RichTextStyle.ITALIC) != 0; }
    get isUnderline(): boolean { return (this.style & RichTextStyle.UNDERLINE) != 0; }
    get isCode(): boolean { return (this.style & RichTextStyle.CODE) != 0; }
    appendText(txt: string, style: RichTextStyle): boolean {
        if (this.style == style) {
            this.text = this.text + txt;
            return true;
        } else {
            return false;
        }
    }
    toM(owner: MContentWithText, mc: ModelCompute): MRichSpan {
        const result = new MRichSpan(owner);
        mc.SetDefaultString(result.Text, this.text);
        result.Style.setValue(mc, this.style);
        return result;
    }
    analyzeReferences(owner: MContentWithText, chapters: Array<MChapter>, articles: Array<MArticle>, sections: Array<MSection>): void {
        // rien
    }
    setText(prv: RichTextItem | undefined, nxt: RichTextItem | undefined, chapters: Map<MChapter, number>, articles: Map<MArticle, number>, sections: Map<MSection, number>): string | undefined {
        let result = "";

        if ((this.style & RichTextStyle.UNDERLINE) != 0 && (prv === undefined || !prv.isUnderline)) {
            result += "[u]";
        }
        if ((this.style & RichTextStyle.BOLD) != 0 && (prv === undefined || !prv.isBold)) {
            result += "[b]";
        }
        if ((this.style & RichTextStyle.ITALIC) != 0 && (prv === undefined || !prv.isItalic)) {
            result += "[i]";
        }
        if ((this.style & RichTextStyle.CODE) != 0 && (prv === undefined || !prv.isCode)) {
            result += "[c]";
        }

        result += this.text;

        if ((this.style & RichTextStyle.CODE) != 0 && (nxt === undefined || !nxt.isCode)) {
            result += "[/c]";
        }
        if ((this.style & RichTextStyle.ITALIC) != 0 && (nxt === undefined || !nxt.isItalic)) {
            result += "[/i]";
        }
        if ((this.style & RichTextStyle.BOLD) != 0 && (nxt === undefined || !nxt.isBold)) {
            result += "[/b]";
        }
        if ((this.style & RichTextStyle.UNDERLINE) != 0 && (nxt === undefined || !nxt.isUnderline)) {
            result += "[/u]";
        }

        return result;
    }
}

class SpanCache extends RichTextCache<RichSpan> {
    constructor() {
        super();
    }
    protected cstr(): RichSpan {
        return new RichSpan();
    }
    get(style: RichTextStyle, text: string): RichSpan {
        const result = this.GetItem();
        result.setup(style, text);
        return result;
    }
}

export class RichBracket extends RichTextItem {

    private symbol: string = "";
    private refID: string = "";
    private chapter: MChapter | undefined;
    private article: MArticle | undefined;
    private section: MSection | undefined;
    private name: string = "";

    constructor() {
        super();
    }
    setup(symbol: string, name: string) {
        this.symbol = symbol;
        this.refID = name;
        this.chapter = undefined;
        this.article = undefined;
        this.section = undefined;
    }

    get isBold(): boolean { return false; }
    get isItalic(): boolean { return false; }
    get isUnderline(): boolean { return false; }
    get isCode(): boolean { return false; }
    appendText(txt: string, style: RichTextStyle): boolean {
        return false;
    }

    private charToDigit(index: number): number {
        const code = this.refID.charCodeAt(index) - 48;
        if (0 <= code && code <= 9) return code;
        else return -1;
    }

    toM(owner: MContentWithText, mc: ModelCompute): MRichTextItem {
        if (this.symbol.toLowerCase() == "link") {
            const firstWhiteSpace = this.refID.indexOf(" ");
            if (firstWhiteSpace >= 0) {
                const href = this.refID.substring(0, firstWhiteSpace);
                if (firstWhiteSpace < this.refID.length - 1) {
                    const display = this.refID.substring(firstWhiteSpace + 1).trim();
                    return new MRichLink(owner, href, display);
                } else {
                    return new MRichLink(owner, href, undefined);
                }
            } else {
                return new MRichLink(owner, this.refID, undefined);
            }
        } else {
            let index = 0;
            let i = 0;
            let name: string | undefined;
            while (i < this.refID.length) {
                const digit = this.charToDigit(i);
                if (digit >= 0) {
                    index = 10 * index + digit;
                    i++;
                } else {
                    name = this.refID.substring(i + 1).trim();
                    break;
                }
            }
            if (this.symbol == "c" || this.symbol == "C") {
                if (index <= owner.ChapterRefs.Count) {
                    return new MRichChapterReference(owner, owner.ChapterRefs.Get(index - 1), name, mc);
                }
            } else if (this.symbol == "a" || this.symbol == "A") {
                if (index <= owner.ArticleRefs.Count) {
                    return new MRichArticleReference(owner, owner.ArticleRefs.Get(index - 1), name, mc);
                }
            } else if (this.symbol == "s" || this.symbol == "S") {
                if (index <= owner.SectionRefs.Count) {
                    return new MRichSectionReference(owner, owner.SectionRefs.Get(index - 1), name, mc);
                }
            }
            const fallback = new MRichSpan(owner);
            mc.SetDefaultString(fallback.Text, "{" + this.symbol + "-" + this.refID + "}")
            return fallback;
        }
    }

    analyzeReferences(owner: MContentWithText, chapters: Array<MChapter>, articles: Array<MArticle>, sections: Array<MSection>): void {
        if (this.symbol == "r" || this.symbol == "R") {
            let i = 0;
            let itemIndex = 0;
            while (i < this.refID.length) {
                let digit = this.charToDigit(i);
                if (digit >= 0) {
                    itemIndex = itemIndex * 10 + digit;
                    i++;
                } else {
                    if (0 < itemIndex && itemIndex <= (owner.Client as DocClient).DocRoot.Chapters.Count) {
                        this.chapter = (owner.Client as DocClient).DocRoot.Chapters.Get(itemIndex - 1);
                        if (this.refID[i] == ".") {
                            i++;
                            itemIndex = 0;
                            while (i < this.refID.length) {
                                digit = this.charToDigit(i);
                                if (digit >= 0) {
                                    itemIndex = itemIndex * 10 + digit;
                                    i++;
                                } else {
                                    if (0 < itemIndex && itemIndex <= this.chapter.Articles.Count) {
                                        this.article = this.chapter.Articles.Get(itemIndex - 1);
                                        if (this.refID[i] == ".") {
                                            i++;
                                            itemIndex = 0;
                                            while (i < this.refID.length) {
                                                digit = this.charToDigit(i);
                                                if (digit >= 0) {
                                                    itemIndex = itemIndex * 10 + digit;
                                                    i++;
                                                } else {
                                                    if (0 < itemIndex && itemIndex <= this.article.Sections.Count) {
                                                        this.section = this.article.Sections.Get(itemIndex - 1);
                                                        sections.push(this.section);
                                                        this.name = this.refID.substring(i + 1);
                                                    }
                                                    return;
                                                }
                                            }
                                            if (0 < itemIndex && itemIndex <= this.article.Sections.Count) {
                                                this.section = this.article.Sections.Get(itemIndex - 1);
                                                sections.push(this.section);
                                                this.name = "";
                                            }
                                        } else {
                                            articles.push(this.article);
                                            this.name = this.refID.substring(i + 1);
                                        }
                                    }
                                    return;
                                }
                            }
                            if (0 < itemIndex && itemIndex <= this.chapter.Articles.Count) {
                                this.article = this.chapter.Articles.Get(itemIndex - 1);
                                articles.push(this.article);
                                this.name = "";
                            }
                        } else {
                            chapters.push(this.chapter);
                            this.name = this.refID.substring(i + 1);
                        }
                    }
                    return;
                }
            }
            if (0 < itemIndex && itemIndex <= (owner.Client as DocClient).DocRoot.Chapters.Count) {
                this.chapter = (owner.Client as DocClient).DocRoot.Chapters.Get(itemIndex - 1);
                chapters.push(this.chapter);
                this.name = "";
            }
        } else if (this.symbol == "C" || this.symbol == "A" || this.symbol == "S" || this.symbol == "c" || this.symbol == "a" || this.symbol == "s") {
            let i = 0;
            let itemIndex = 0;
            this.name = "";
            while (i < this.refID.length) {
                let digit = this.charToDigit(i);
                if (digit >= 0) {
                    itemIndex = itemIndex * 10 + digit;
                    i++;
                } else {
                    this.name = this.refID.substring(i + 1);
                    break;
                }
            }
            if (0 < itemIndex) {
                if (this.symbol == "C" || this.symbol == "c") {
                    if (itemIndex <= owner.ChapterRefs.Count) {
                        this.chapter = owner.ChapterRefs.Get(itemIndex - 1);
                        chapters.push(this.chapter);
                    }
                } else if (this.symbol == "A" || this.symbol == "a") {
                    if (itemIndex <= owner.ArticleRefs.Count) {
                        this.article = owner.ArticleRefs.Get(itemIndex - 1);
                        articles.push(this.article);
                    }
                } else {
                    if (itemIndex <= owner.SectionRefs.Count) {
                        this.section = owner.SectionRefs.Get(itemIndex - 1);
                        sections.push(this.section);
                    }
                }
            }
        }
    }
    setText(prv: RichTextItem | undefined, nxt: RichTextItem | undefined, chapters: Map<MChapter, number>, articles: Map<MArticle, number>, sections: Map<MSection, number>): string | undefined {
        if (this.section === undefined) {
            if (this.article === undefined) {
                if (this.chapter !== undefined) {
                    const chapterID = chapters.get(this.chapter);
                    if (chapterID !== undefined) {
                        return "{C-" + chapterID + " " + this.name + "}";
                    }
                }
            } else {
                const articleID = articles.get(this.article);
                if (articleID !== undefined) {
                    return "{A-" + articleID + " " + this.name + "}";
                }
            }
        } else {
            const sectionID = sections.get(this.section);
            if (sectionID !== undefined) {
                return "{S-" + sectionID + " " + this.name + "}";
            }
        }
        return "{" + this.symbol + "-" + this.refID + "}";
    }
}

class BracketCache extends RichTextCache<RichBracket> {
    constructor() {
        super();
    }
    protected cstr(): RichBracket {
        return new RichBracket();
    }
    get(symbol: string, name: string): RichBracket {
        const result = this.GetItem();
        result.setup(symbol, name);
        return result;
    }
}

export class RichLineBreak extends RichTextItem {
    constructor() {
        super();
    }
    get isBold(): boolean { return false; }
    get isItalic(): boolean { return false; }
    get isUnderline(): boolean { return false; }
    get isCode(): boolean { return false; }
    appendText(txt: string, style: RichTextStyle): boolean {
        return false;
    }
    toM(owner: MContentWithText, mc: ModelCompute): MRichLineBreak {
        return new MRichLineBreak(owner);
    }
    analyzeReferences(owner: MContentWithText, chapters: Array<MChapter>, articles: Array<MArticle>, sections: Array<MSection>): void {
        // rien
    }
    setText(prv: RichTextItem | undefined, nxt: RichTextItem | undefined, chapters: Map<MChapter, number>, articles: Map<MArticle, number>, sections: Map<MSection, number>): string | undefined {
        return "\n";
    }
}

export abstract class MRichTextItem extends MVirtualObject {

    constructor(owner: MContentWithText) {
        super(owner);
    }

    abstract makeView(para: HTMLParagraphElement, owner: VContent): VRichTextItem;
    abstract toHTML(): string;
    abstract toMarkdown(inTable: boolean): string;
}

export class RichTextStyleProperty extends EnumVirtualProperty {
    constructor(owner: MRichSpan) {
        super(owner, undefined);
    }
    /** @internal */
    setValue(mc: ModelCompute, val: RichTextStyle) {
        this.SetDefaultValue(mc, val);
    }
    public get Value(): RichTextStyle { return this.ValueByte as RichTextStyle };

    public get IsBold(): boolean { return (this.Value & RichTextStyle.BOLD) != 0; }
    public get IsItalic(): boolean { return (this.Value & RichTextStyle.ITALIC) != 0; }
    public get IsUnderline(): boolean { return (this.Value & RichTextStyle.UNDERLINE) != 0; }
    public get IsCode(): boolean { return (this.Value & RichTextStyle.CODE) != 0; }
}

export class MRichSpan extends MRichTextItem {

    public readonly Text: StringVirtualProperty;
    public readonly Style: RichTextStyleProperty;

    constructor(owner: MContentWithText) {
        super(owner);
        this.Text = new StringVirtualProperty(this, undefined);
        this.Style = new RichTextStyleProperty(this);
    }

    makeView(para: HTMLParagraphElement, owner: VContent): VRichTextItem {
        return new VSpan(this, para);
    }

    toHTML(): string {
        if (this.Style.IsCode) {
            return '<code>' + this.Text.Value + '</code>';
        } else {
            let html = '<span';
            if (this.Style.IsBold || this.Style.IsItalic || this.Style.IsUnderline) {
                html += ' style="';
                if (this.Style.IsBold) {
                    html += 'font-weight: bold;';
                }
                if (this.Style.IsItalic) {
                    html += 'font-style: italic;';
                }
                if (this.Style.IsUnderline) {
                    html += 'text-decoration: underline;';
                }
                html += '"';
            }
            return html + '>' + this.Text.Value + '</span>';
        }
    }
    toMarkdown(inTable: boolean): string {
        if (this.Style.IsCode) {
            return '`' + this.Text.Value + '`';
        } else {
            if (this.Style.IsBold) {
                if (this.Style.IsItalic) {
                    return '***' + this.Text.Value + '***';
                } else {
                    return '**' + this.Text.Value + '**';
                }
            } else if (this.Style.IsItalic) {
                return '*' + this.Text.Value + '*';
            } else if (this.Style.IsUnderline) {
                return '__' + this.Text.Value + '__';
            } else {
                return this.Text.Value;
            }
        }
    }
}

export class MRichLineBreak extends MRichTextItem {
    constructor(owner: MContentWithText) {
        super(owner);
    }

    makeView(para: HTMLParagraphElement, owner: VContent): VRichTextItem {
        return new VLineBreak(this, para);
    }

    toHTML(): string {
        return '<br>';
    }
    toMarkdown(inTable: boolean): string {
        if (inTable) return '<br>';
        else return '  \n';
    }
}

export class MRichLink extends MRichTextItem {
    public readonly HRef: string;
    public readonly DisplayName: string;

    constructor(owner: MContentWithText, href: string, display: string | undefined) {
        super(owner);
        this.HRef = href;
        if (display && display.length > 0) {
            this.DisplayName = display;
        } else {
            this.DisplayName = href;
        }
    }

    makeView(para: HTMLParagraphElement, owner: VContent): VRichTextItem {
        return new VLink(this, para);
    }

    toHTML(): string {
        return '<a href="' + this.HRef + '">' + this.DisplayName + '</a>';
    }
    toMarkdown(inTable: boolean): string {
        return '[' + this.DisplayName + '](' + this.HRef + ')';
    }
}

export abstract class MRichReference extends MRichTextItem {
    public readonly Name: StringVirtualProperty;
    constructor(owner: MContentWithText) {
        super(owner);
        this.Name = new StringVirtualProperty(this, undefined);
    }
    protected makeMarkdown(name: StringRealProperty, numberRef: string): string {
        if (name.Value == this.Name.Value) {
            return '{[' + numberRef + '] ' + this.Name.Value + '}';
        } else {
            return this.Name.Value + ' ({[' + numberRef + '] ' + name.Value + '})';
        }
    }
}
export class MRichChapterReference extends MRichReference {
    public readonly Chapter: MChapter;
    constructor(owner: MContentWithText, chapter: MChapter, name: string | undefined, mc: ModelCompute) {
        super(owner);
        this.Chapter = chapter;
        if (name === undefined || name.length <= 0) {
            this.Name.BindDefaultTo(chapter.Name, mc);
        } else {
            mc.SetDefaultString(this.Name, name);
        }
    }
    makeView(para: HTMLParagraphElement, owner: VContent): VRichTextItem {
        return new VRichChapter(this, para, owner.Parent.Parent.Parent.Parent);
    }

    toHTML(): string {
        return '<a href="#' + this.Chapter.ID + '">' + this.Name.Value + '</a>';
    }
    toMarkdown(inTable: boolean): string {
        return this.makeMarkdown(this.Chapter.Name, this.Chapter.NumberIndexing.Value.toString());
    }
}
export class MRichArticleReference extends MRichReference {
    public readonly Article: MArticle;
    constructor(owner: MContentWithText, article: MArticle, name: string | undefined, mc: ModelCompute) {
        super(owner);
        this.Article = article;
        if (name === undefined || name.length <= 0) {
            this.Name.BindDefaultTo(article.Name, mc);
        } else {
            mc.SetDefaultString(this.Name, name);
        }
    }
    makeView(para: HTMLParagraphElement, owner: VContent): VRichTextItem {
        return new VRichArticle(this, para);
    }

    toHTML(): string {
        return '<a href="#' + this.Article.ID + '">' + this.Name.Value + '</a>';
    }
    toMarkdown(inTable: boolean): string {
        return this.makeMarkdown(this.Article.Name, this.Article.Parent.NumberIndexing.Value + '.' + this.Article.NumberIndexing.Value);
    }
}
export class MRichSectionReference extends MRichReference {
    public readonly Section: MSection;
    constructor(owner: MContentWithText, section: MSection, name: string | undefined, mc: ModelCompute) {
        super(owner);
        this.Section = section;
        if (name === undefined || name.length <= 0) {
            this.Name.BindDefaultTo(section.Name, mc);
        } else {
            mc.SetDefaultString(this.Name, name);
        }
    }
    makeView(para: HTMLParagraphElement, owner: VContent): VRichTextItem {
        return new VRichSection(this, para);
    }

    toHTML(): string {
        return '<a href="#' + this.Section.ID + '">' + this.Name.Value + '</a>';
    }
    toMarkdown(inTable: boolean): string {
        return this.makeMarkdown(this.Section.Name, this.Section.Parent.Parent.NumberIndexing.Value + '.' + this.Section.Parent.NumberIndexing.Value + '.' + this.Section.NumberIndexing.Value);
    }
}